import { Inject, Injectable } from "@angular/core";
import { Observable, of, pipe } from "rxjs";
import { catchError, map, mergeMap, tap } from 'rxjs/operators'
import { AppMetaData, CalendarReferenceEntity, CheckRideGroupAllItems, CheckRideRequestCalendarQueryEntity, CheckRideRequestEntity, CheckRideRequestSubStatusAllItems, CheckRideTypeAllItems, CheckrideActiveApplicantCheckridesQueryEntity, PilotExaminerEntity, PilotExaminerLocationEntity, PilotExaminerSearchQueryEntity, PilotExaminerStatusAllItems, StudentApplicantEntity } from "../../../meta-data/app-meta-data.service";
import { DwMetaDataServiceToken, DwMetaDataService, DwOrmDataService, DwOrmDataServiceToken, DwQuery, deepCopy, DwConfigService, DwConfigServiceToken, DwLookupInfo, SuppressErrorMessagesHeader, DwSecurityUserService, DwQueryMetaData } from "@devwareapps/devware-cap";
import { CheckrideValidationResult } from "../models/checkride-validation-result.model";
import { HttpClient, HttpHeaders } from "@angular/common/http";
import { CheckrideValidationRequest } from "../models/checkride-validation-request.model";


@Injectable({ providedIn: 'root' })
export class ApplicantRepositoryService {
    apiRoot: string;

    constructor(
        @Inject(DwOrmDataServiceToken) private dwOrmDataService: DwOrmDataService,
        @Inject(DwMetaDataServiceToken) private dwMetaDataService: DwMetaDataService,
        @Inject(DwConfigServiceToken) configService: DwConfigService,
        private dwSecurityUserService: DwSecurityUserService,
        private httpClient: HttpClient
    ) {
        this.apiRoot = configService.coreConfig.apiRoot;
    }

    getApplicantEmails(studentApplicantId: number, flightSchoolApplicantId: number): Observable<string[]> {

        const emails: string[] = [];

        let ob: Observable<any> = of(null);
        if (flightSchoolApplicantId && !studentApplicantId) {
            const query = AppMetaData.FlightSchoolApplicant.CreateQueryBuilder()
                .addFilterAnd(f => f.Equal(a => a.FlightSchoolApplicantId, flightSchoolApplicantId));

            ob = this.dwOrmDataService.executeQuerySingle(query)
                .pipe(tap(flightSchoolApplicant => {
                    studentApplicantId = flightSchoolApplicant?.StudentApplicantId;
                }));
        }

        return ob.pipe(mergeMap(flightSchoolApplicant => {
            if (flightSchoolApplicant) {
                studentApplicantId = flightSchoolApplicant.StudentApplicantId;

                emails.push(flightSchoolApplicant?.Email);
            }

            if (studentApplicantId) {
                return AppMetaData.Queries.StudentApplicantWithDetail.CreateQueryBuilder(this.dwMetaDataService)
                    .pipe(mergeMap(query => {
                        query.addFilterAnd(f => f.Equal(a => a.StudentApplicantId, studentApplicantId));

                        return this.dwOrmDataService.executeQuerySingle(query)
                            .pipe(map(result => {

                                if (result?.DwUserEmail) {
                                    emails.push(result?.DwUserEmail);
                                }

                                return emails;
                            }));
                    }));
            }

            return of(emails);
        }));
    }



    getPilotExaminer(pilotExaminerId: number, includeCalendarRef: boolean = false): Observable<PilotExaminerEntity> {
        const query = AppMetaData.PilotExaminer.CreateQueryBuilder();

        query.addFilterAnd(filters => filters.Equal(field => field.PilotExaminerId, pilotExaminerId));

        return this.dwOrmDataService.executeQuerySingle(query);
    }

    getActiveCheckrides(currentCheckrideRequestId: number, ...emails: string[]): Observable<CheckrideActiveApplicantCheckridesQueryEntity[]> {
        return AppMetaData.Queries.CheckrideActiveApplicantCheckrides.CreateQueryBuilder(this.dwMetaDataService)
            .pipe(mergeMap(query => {
                query.query = this.cloneQuery(query.query);

                if (!emails || emails.length == 0) {
                    return of([]);
                }

                // TODO: This is just checking the student applicant (which might be fine), but could also locate by applicant placeholder email and flight school applicant email
                //   Note: In a perfect world, all three would be tied to the student applicant id when their account is created and going forward would always have an applicant id
                query.addFilterAnd(f => f.GroupOr(
                    g => g.InList(a => a.RoAppFlightSchoolApplicantEmail, emails),
                    g => g.InList(a => a.RoAppStudentApplicantEmailManual, emails),
                    g => g.InList(a => a.RoAppStudentApplicantEmail, emails),
                ));

                query.addFilterAnd(f => f.InList(a => a.RoAppStudentApplicantEmail, emails));

                if (currentCheckrideRequestId) {
                    query.addFilterAnd(f => f.NotEqual(a => a.CheckRideRequestId, currentCheckrideRequestId));
                }

                return this.dwOrmDataService.executeQuery(query)
            }));
    }

    // getActiveCheckridesMultiple(emails: string[]): Observable<CheckrideActiveApplicantCheckridesQueryEntity[]> {
    //     return AppMetaData.Queries.CheckrideActiveApplicantCheckrides.CreateQueryBuilder(this.dwMetaDataService)
    //         .pipe(mergeMap(query => {
    //             query.query = this.cloneQuery(query.query);

    //             query.addFilterAnd(f => f.GroupOr(
    //                 g => g.InList(a => a.RoAppStudentApplicantEmail, emails),
    //                 g => g.InList(a => a.RoAppStudentApplicantEmailManual, emails),
    //                 g => g.InList(a => a.RoAppFlightSchoolApplicantEmail, emails),
    //             ));

    //             return this.dwOrmDataService.executeQuery(query)
    //         }));
    // }

    validateActiveCheckrides(checkrideRequest: CheckRideRequestEntity): Observable<CheckrideValidationResult> {
        const url = `${this.apiRoot}/pilexos/booking/validate-checkride`;

        const request: CheckrideValidationRequest = {
            CheckRideRequestJSON: JSON.stringify(checkrideRequest)
        }

        let headers = new HttpHeaders();

        headers = headers.append(SuppressErrorMessagesHeader, 'true');

        return this.httpClient.post<CheckrideValidationResult>(url, request, { headers: headers })
            .pipe(catchError(error => {
                const result: CheckrideValidationResult = {
                    IsSuccess: false,
                    Message: `An error occurred while validating the checkride request.`
                };

                return of(result);
            }));
    }

    getCurrentApplicantinfo(includeHomeDetails: boolean = false, skipCheckrides: boolean = false): Observable<StudentApplicantEntity> {
        const query = AppMetaData.StudentApplicant.CreateQueryBuilder();

        query.addFilterAnd(filters => filters.Equal(field => field.UserId, this.dwSecurityUserService.securityContext.ApplicationUser?.UserId));

        query.query.FieldSettings.IncludeRootItemFields = false;

        query.addFields(f => [f.ApplicantAddressId, f.UserId, f.ApplicationDescription, f.AviationAuthorityId, f.PilotCertificateNumber, f.Citizenship]);

        // Build user query
        var userQuery = AppMetaData.DwUser.CreateQueryBuilder();

        userQuery.query.FieldSettings.IncludeRootItemFields = false;

        userQuery.addFields(f => [f.FirstName, f.LastName, f.UserPictureMediaId]);

        query.addPrefetch(r => r.DwUser, userQuery.query);

        // Build checkride query
        const checkRideQuery = AppMetaData.CheckRideRequest.CreateQueryBuilder();

        checkRideQuery.query.FieldSettings.IncludeRootItemFields = false;

        checkRideQuery.addFields(f => [f.ScheduledPilotExaminerId, f.CheckRideRequestSubStatusId, f.StudentApplicantId, f.ScheduledDateTime]);
        if (includeHomeDetails) {
            checkRideQuery.query.FieldSettings.LoadAllLookupDisplayFields = true;
            checkRideQuery.addFields(f => [f.CheckRideTypeId, f.CheckRideTestTypeId, f.CheckRideGroupId,
                f.ScheduledAirportId, f.ScheduledTimeMinutes, f.ScheduledCheckRideCost,
                f.AirplaneMakeModel, f.AuthorityApplicationNumber, f.FlightSchoolId,
                f.FlightSchoolName, f.FlightSchoolNameOther]);
        }
        const excludedStatuses = includeHomeDetails
            ? [CheckRideRequestSubStatusAllItems.Deleted]
            : [CheckRideRequestSubStatusAllItems.PendingApprovalPayment, CheckRideRequestSubStatusAllItems.Cancelled, CheckRideRequestSubStatusAllItems.Deleted];
        checkRideQuery.addFilterAnd(f => f.NotInList(f => f.CheckRideRequestSubStatusId, excludedStatuses));
        // Check the result as well
        checkRideQuery.addPrefetch(r => r.CheckRideResult);

        if (!skipCheckrides) {
            query.addPrefetch(r => r.CheckRideRequest, checkRideQuery.query);
        }

        return this.dwOrmDataService.executeQuerySingle(query);
    }

    getApplicantCheckrideRequest(checkrideRequestId: number): Observable<CheckRideRequestEntity> {
        const checkRideQuery = AppMetaData.CheckRideRequest.CreateQueryBuilder();

        checkRideQuery.addFilterAnd(filters => filters.Equal(field => field.CheckRideRequestId, checkrideRequestId));

        checkRideQuery.addRelation(r => r.StudentApplicant);

        checkRideQuery.addFilterAnd(filters => filters.Equal(field => AppMetaData.StudentApplicant.Attributes.UserId, this.dwSecurityUserService.securityContext.ApplicationUser?.UserId));

        // Check the result as well
        checkRideQuery.addPrefetch(r => r.CheckRideResult);
        checkRideQuery.addPrefetch(r => r.CheckRidePayment);

        return this.dwOrmDataService.executeQuerySingle(checkRideQuery);
    }


    cloneQuery(query: DwQuery): DwQuery {
        const newQuery = new DwQuery(query.RootTable);

        const clonedQuery = deepCopy(query);

        return Object.assign(newQuery, clonedQuery);
    }
}
